import {
  confirmOlaUpload,
  createOlaBatch,
  type AssetUploadMetadata,
  type CreateOlaBatchResponse,
} from '../../api';
import {
  describeError,
  isAbort,
  putFileToSignedUrl,
  type BatchUploadDeps,
  type BatchUploadResult,
} from './batchUpload';
import { isUploadable, type DetectedAd } from './olaDetect';

/**
 * The OLA analogue of `runBatchUpload`: one folder per asset, many PUTs each.
 *
 * Kept beside it rather than folded into it. The two share `putFileToSignedUrl`
 * and `describeError`, but nothing else: the unit of progress here is an ad
 * rather than a file, a partial ad must not be confirmed, and one failed member
 * fails its whole banner while leaving the other banners alone.
 *
 * Progress is reported per ad, not per file. A four-banner upload is four rows;
 * the same thing as sixty file rows is unreadable, and a half-uploaded banner
 * is not a partial success the user can do anything with anyway.
 */

/** Ads at a time. Low, because each one is already fanning out internally. */
const AD_CONCURRENCY = 2;

/** Members of one ad at a time. Small files, so the win is in the count. */
const MEMBER_CONCURRENCY = 4;

/** Matching `batchUpload.ts`: the last tenth of the bar belongs to confirm. */
const PUT_PROGRESS_CEILING = 90;

export interface OlaUploadItem {
  /** The ad's name, which is also the asset's `filename_original`. */
  name: string;
  fileCount: number;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  /** 0–100 across the ad's bytes, reaching 100 only after confirm. */
  progress: number;
  error?: string;
  duplicateOf?: string[];
  /**
   * How many local references the backend could not resolve.
   *
   * Surfaced because it is the one thing this whole flow exists to catch: the
   * banner uploaded fine and will still render with a hole in it.
   */
  warningCount?: number;
}

export interface OlaUploadDeps {
  createOlaBatch: typeof createOlaBatch;
  confirmOlaUpload: typeof confirmOlaUpload;
  putFile: BatchUploadDeps['putFile'];
}

const DEFAULT_DEPS: OlaUploadDeps = {
  createOlaBatch,
  confirmOlaUpload,
  putFile: putFileToSignedUrl,
};

export interface RunOlaUploadOptions {
  ads: DetectedAd[];
  label?: string | null;
  metadata?: AssetUploadMetadata | null;
  signal?: AbortSignal;
  onChange: (items: OlaUploadItem[]) => void;
  deps?: Partial<OlaUploadDeps>;
}

export async function runOlaUpload({
  ads,
  label,
  metadata,
  signal,
  onChange,
  deps: overrides,
}: RunOlaUploadOptions): Promise<BatchUploadResult> {
  const deps = { ...DEFAULT_DEPS, ...overrides };

  const items: OlaUploadItem[] = ads.map((ad) => ({
    name: ad.name,
    fileCount: ad.files.length,
    status: 'pending',
    progress: 0,
  }));
  const publish = () => onChange(items.map((item) => ({ ...item })));

  if (ads.length === 0) {
    return { batchId: null, uploaded: 0, failed: 0, cancelled: false };
  }

  if (ads.some((ad) => !isUploadable(ad))) {
    for (const [index, ad] of ads.entries()) {
      items[index].status = 'failed';
      items[index].error = isUploadable(ad)
        ? 'Another OLA in this batch is invalid'
        : ad.issues.join('; ');
    }
    publish();
    return { batchId: null, uploaded: 0, failed: items.length, cancelled: false };
  }

  publish();

  let batch: CreateOlaBatchResponse;
  try {
    batch = await deps.createOlaBatch(
      {
        ads: ads.map((ad) => ({
          name: ad.name,
          entry_relpath: ad.entryRelpath,
          poster_relpath: ad.posterRelpath!,
          files: ad.files.map(({ relpath, file }) => ({ relpath, size: file.size })),
        })),
        label: label?.trim() ? label.trim() : null,
        metadata: metadata ?? null,
      },
      signal,
    );
  } catch (error) {
    const cancelled = isAbort(error);
    const reason = cancelled ? 'Cancelled' : describeError(error, 'Could not start the upload');
    for (const item of items) {
      item.status = 'failed';
      item.error = reason;
    }
    publish();
    return { batchId: null, uploaded: 0, failed: items.length, cancelled };
  }

  // The backend builds `ads` by iterating the request's list, so index i is the
  // target for ads[i]. Checked by name rather than by length for the same
  // reason the file flow checks by filename: a reorder would otherwise send
  // every banner's files to another banner's asset and raise nothing.
  const mispaired =
    batch.ads.length !== ads.length || ads.some((ad, index) => batch.ads[index].name !== ad.name);

  if (mispaired) {
    for (const item of items) {
      item.status = 'failed';
      item.error = 'Server returned a mismatched upload plan';
    }
    publish();
    return { batchId: batch.batch_id, uploaded: 0, failed: items.length, cancelled: false };
  }

  let cancelled = false;

  const uploadAd = async (index: number): Promise<void> => {
    const ad = ads[index];
    const target = batch.ads[index];
    const item = items[index];

    item.status = 'uploading';
    publish();

    const byRelpath = new Map(ad.files.map((member) => [member.relpath, member.file]));
    // Bytes done per member, so the bar tracks the ad's real weight rather
    // than its file count: a 4 MB background and a 2 KB stylesheet are not
    // the same quarter of the work.
    const doneBytes = new Map<string, number>();

    const reportProgress = () => {
      let sent = 0;
      for (const bytes of doneBytes.values()) sent += bytes;
      item.progress = Math.round((sent / Math.max(1, ad.totalBytes)) * PUT_PROGRESS_CEILING);
      publish();
    };

    let cursor = 0;
    const putNext = async (): Promise<void> => {
      const memberIndex = cursor++;
      if (memberIndex >= target.uploads.length) return;

      const upload = target.uploads[memberIndex];
      const file = byRelpath.get(upload.relpath);
      if (!file) throw new Error(`Server asked for a file that was not offered: ${upload.relpath}`);

      await deps.putFile(upload.upload_url, file, upload.content_type, {
        signal,
        onProgress: (percent) => {
          doneBytes.set(upload.relpath, (file.size * percent) / PUT_PROGRESS_CEILING);
          reportProgress();
        },
      });
      doneBytes.set(upload.relpath, file.size);
      reportProgress();
    };

    const worker = async () => {
      while (cursor < target.uploads.length) await putNext();
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(MEMBER_CONCURRENCY, target.uploads.length) }, () => worker()),
      );

      // Only once every member is up. Confirming early 400s and, worse, would
      // be the point at which a banner missing a font could be marked staged.
      const confirmed = await deps.confirmOlaUpload(target.asset_id, signal);

      item.status = 'done';
      item.progress = 100;
      if (confirmed.duplicate_of.length > 0) item.duplicateOf = confirmed.duplicate_of;
      const warnings = confirmed.asset.ola_report?.warnings ?? [];
      if (warnings.length > 0) item.warningCount = warnings.length;
      publish();
    } catch (error) {
      if (isAbort(error)) {
        cancelled = true;
        item.status = 'failed';
        item.error = 'Cancelled';
        publish();
        return;
      }
      // Deliberately not confirmed. The asset row stays `uploading`, which
      // makes it invisible everywhere and reapable by the bucket's lifecycle
      // rule — the same fate as an abandoned upload, which is what this is.
      cursor = target.uploads.length;
      item.status = 'failed';
      item.error = describeError(error, 'Upload failed');
      publish();
    }
  };

  let adCursor = 0;
  const adWorker = async () => {
    while (adCursor < ads.length) {
      const index = adCursor++;
      if (cancelled) {
        // Whatever had not started is not going to. Failed ads keep their own
        // reason: overwriting it with "Cancelled" loses the diagnosis.
        if (items[index].status === 'pending') {
          items[index].status = 'failed';
          items[index].error = 'Cancelled';
          publish();
        }
        continue;
      }
      await uploadAd(index);
    }
  };

  await Promise.all(Array.from({ length: Math.min(AD_CONCURRENCY, ads.length) }, () => adWorker()));

  const uploaded = items.filter((item) => item.status === 'done').length;

  return {
    batchId: batch.batch_id,
    uploaded,
    failed: items.length - uploaded,
    cancelled,
  };
}
